import { isRegisterableAccelerator } from '@electron/shared/keyboard-accelerator';
import {
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { fmtKeyToSymbol, type KeyName } from '@/lib/key';
import {
  getDisallowedShortcut,
  getDisallowedShortcutMessage,
} from '@/lib/keyboard-shortcuts';
import { cn } from '@/lib/utils';

import { DropdownMenu } from './dropdown-menu';
import { Input } from './Input';

type ShortcutOption =
  | 'double-tap-option'
  | 'option-space'
  | 'double-tap-command'
  | 'custom';
type ShortcutPreset = Exclude<ShortcutOption, 'custom'>;

const PRESET_SHORTCUTS: Record<ShortcutPreset, string> = {
  'double-tap-option': 'option+option',
  'option-space': 'option+space',
  'double-tap-command': 'command+command',
};

const ALL_PRESETS: readonly ShortcutPreset[] = [
  'double-tap-option',
  'option-space',
];

const SHORTCUT_OPTION_LABELS: Record<ShortcutOption, string> = {
  'double-tap-option': 'Tap Option twice',
  'option-space': 'Option + Space',
  'double-tap-command': 'Tap Command twice',
  custom: 'Custom',
};

const keyMap: Record<string, string> = {
  Meta: 'command',
  Command: 'command',
  Control: 'control',
  Ctrl: 'control',
  Alt: 'option',
  Option: 'option',
  Shift: 'shift',
};

const codeKeyMap: Record<string, string> = {
  Space: 'space',
  Enter: 'enter',
  NumpadEnter: 'enter',
  Tab: 'tab',
  Backspace: 'backspace',
  Delete: 'delete',
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
};

const MODIFIER_KEYS = new Set(['Meta', 'Control', 'Alt', 'Shift']);

const normalizeKey = (key: string): string => {
  if (keyMap[key]) {
    return keyMap[key];
  }
  return key.toLowerCase();
};

/**
 * The physical key wins because macOS puts the Option-composed character in `key`, and
 * Electron cannot parse `option+∂`. Punctuation has no layout-independent code mapping,
 * so it falls back to `key`, which is correct whenever Option is not held.
 */
const normalizeKeyFromCode = (code: string, key: string): string | null => {
  if (codeKeyMap[code]) return codeKeyMap[code];
  if (/^Key[A-Z]$/.test(code)) return code.slice(3).toLowerCase();
  if (/^Digit\d$/.test(code)) return code.slice(5);
  if (/^F\d{1,2}$/.test(code)) return code.toLowerCase();
  return key.length === 1 ? key.toLowerCase() : null;
};

const formatShortcutForDisplay = (shortcut: string): string => {
  return shortcut
    .split('+')
    .map((key) => fmtKeyToSymbol(key as KeyName))
    .join(' + ');
};

const getOptionFromShortcut = (
  shortcut: string,
  presets: readonly ShortcutPreset[]
): ShortcutOption => {
  const preset = presets.find(
    (option) => PRESET_SHORTCUTS[option] === shortcut
  );
  return preset ?? 'custom';
};

export const KeyboardShortcutInput = ({
  value,
  onChange,
  onRecordingChange,
  disabled = false,
  presets = ALL_PRESETS,
  validate,
}: {
  value: string;
  onChange: (shortcut: string) => void | boolean | Promise<void | boolean>;
  onRecordingChange?: (isRecording: boolean) => void;
  disabled?: boolean;
  /** Pass `[]` for no dropdown: the modifier-tap preset needs the single tap monitor. */
  presets?: readonly ShortcutPreset[];
  /** Return why a combo is unusable here, or null to accept it. */
  validate?: (shortcut: string) => string | null;
}) => {
  const [selectedOption, setSelectedOption] = useState<ShortcutOption>(() =>
    getOptionFromShortcut(value, presets)
  );
  const [customShortcut, setCustomShortcut] = useState(() =>
    getOptionFromShortcut(value, presets) === 'custom' ? value : ''
  );
  const [isRecording, setIsRecording] = useState(false);
  const [recordingHint, setRecordingHint] = useState('Press shortcut');
  const inputRef = useRef<HTMLInputElement>(null);
  const isRecordingRef = useRef(false);
  const lastModifierTapRef = useRef<{ key: string; time: number } | null>(null);
  const nonModifierPressedRef = useRef(false);

  const rejectShortcut = useCallback(
    (shortcut: string): boolean => {
      const disallowed = getDisallowedShortcut(shortcut);
      const message = disallowed
        ? getDisallowedShortcutMessage(disallowed)
        : validate?.(shortcut);
      if (!message) return false;

      toast.error(message);
      return true;
    },
    [validate]
  );

  const notifyRecordingChange = useCallback(
    (recording: boolean) => {
      if (isRecordingRef.current === recording) return;
      isRecordingRef.current = recording;
      onRecordingChange?.(recording);
    },
    [onRecordingChange]
  );

  const commitShortcut = useCallback(
    (shortcut: string) => {
      const result = onChange(shortcut);
      if (!(result instanceof Promise)) return;

      void result.then((accepted) => {
        if (accepted !== false) return;
        const selected = getOptionFromShortcut(value, presets);
        setSelectedOption(selected);
        setCustomShortcut(selected === 'custom' ? value : '');
      });
    },
    [onChange, presets, value]
  );

  const handleOptionChange = useCallback(
    (option: ShortcutOption) => {
      if (option === 'custom') {
        setSelectedOption(option);
        if (customShortcut) commitShortcut(customShortcut);
        return;
      }

      const preset = PRESET_SHORTCUTS[option];
      if (rejectShortcut(preset)) return;

      setSelectedOption(option);
      commitShortcut(preset);
    },
    [commitShortcut, customShortcut, rejectShortcut]
  );

  const handleCustomShortcutChange = useCallback(
    (shortcut: string) => {
      setCustomShortcut(shortcut);
      commitShortcut(shortcut);
    },
    [commitShortcut]
  );

  const stopRecording = useCallback(() => {
    notifyRecordingChange(false);
    setIsRecording(false);
    setRecordingHint('Press shortcut');
    lastModifierTapRef.current = null;
    nonModifierPressedRef.current = false;
  }, [notifyRecordingChange]);

  useEffect(
    () => () => {
      notifyRecordingChange(false);
    },
    [notifyRecordingChange]
  );

  const handleRecordingKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLInputElement>) => {
      if (!isRecording) return;

      e.preventDefault();
      e.stopPropagation();

      if (e.key === 'Escape') {
        stopRecording();
        return;
      }

      if (MODIFIER_KEYS.has(e.key)) {
        return;
      }

      nonModifierPressedRef.current = true;
      lastModifierTapRef.current = null;

      const modifiers: string[] = [];
      if (e.metaKey) modifiers.push('command');
      if (e.ctrlKey) modifiers.push('control');
      if (e.altKey) modifiers.push('option');
      if (e.shiftKey) modifiers.push('shift');

      const key = normalizeKeyFromCode(e.code, e.key);
      if (!key || modifiers.length === 0) {
        return;
      }

      const shortcut = [...modifiers, key].join('+');
      if (!isRegisterableAccelerator(shortcut)) {
        return;
      }

      if (rejectShortcut(shortcut)) {
        stopRecording();
        return;
      }

      handleCustomShortcutChange(shortcut);
      stopRecording();
    },
    [handleCustomShortcutChange, isRecording, rejectShortcut, stopRecording]
  );

  const handleRecordingKeyUp = useCallback(
    (e: ReactKeyboardEvent<HTMLInputElement>) => {
      if (!isRecording || !MODIFIER_KEYS.has(e.key)) return;

      e.preventDefault();
      e.stopPropagation();

      if (nonModifierPressedRef.current) {
        nonModifierPressedRef.current = false;
        return;
      }

      const normalized = normalizeKey(e.key);
      const now = Date.now();
      const lastTap = lastModifierTapRef.current;

      // Still-held modifiers mean this was a chord, and letting one go ends it
      const held: string[] = [];
      if (e.metaKey) held.push('command');
      if (e.ctrlKey) held.push('control');
      if (e.altKey) held.push('option');
      if (e.shiftKey) held.push('shift');

      // A browser may report the released key's own flag, leaving one key after the dedupe
      const chordKeys = [...new Set([...held, normalized])].sort();
      if (chordKeys.length > 1) {
        const chord = chordKeys.join('+');
        if (!rejectShortcut(chord)) {
          handleCustomShortcutChange(chord);
        }
        stopRecording();
        return;
      }

      if (lastTap && lastTap.key === normalized && now - lastTap.time < 400) {
        const shortcut = `${normalized}+${normalized}`;
        if (!rejectShortcut(shortcut)) {
          handleCustomShortcutChange(shortcut);
        }
        stopRecording();
        return;
      }

      lastModifierTapRef.current = { key: normalized, time: now };
    },
    [handleCustomShortcutChange, isRecording, rejectShortcut, stopRecording]
  );

  const handleInputClick = () => {
    if (disabled) return;
    setRecordingHint('Press shortcut');
    lastModifierTapRef.current = null;
    nonModifierPressedRef.current = false;
    notifyRecordingChange(true);
    setIsRecording(true);
    inputRef.current?.focus();
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      stopRecording();
    }, 100);
  };

  const showPresets = presets.length > 0;

  return (
    <div className="flex flex-col gap-2">
      {showPresets && (
        <DropdownMenu.Root>
          <DropdownMenu.Trigger disabled={disabled}>
            <span>{SHORTCUT_OPTION_LABELS[selectedOption]}</span>
            <Icons.chevronDown className="size-3" />
          </DropdownMenu.Trigger>
          <DropdownMenu.Content
            align="end"
            side="bottom"
            sideOffset={8}
            className="w-[12rem]"
          >
            {presets.map((preset) => (
              <DropdownMenu.Item
                key={preset}
                onSelect={() => handleOptionChange(preset)}
              >
                {SHORTCUT_OPTION_LABELS[preset]}
              </DropdownMenu.Item>
            ))}
            <DropdownMenu.Item onSelect={() => handleOptionChange('custom')}>
              {SHORTCUT_OPTION_LABELS['custom']}
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      )}
      {selectedOption === 'custom' && (
        <Input
          ref={inputRef}
          type="text"
          value={
            isRecording
              ? recordingHint
              : formatShortcutForDisplay(customShortcut)
          }
          onClick={handleInputClick}
          onBlur={handleInputBlur}
          onKeyDown={handleRecordingKeyDown}
          onKeyUp={handleRecordingKeyUp}
          readOnly
          disabled={disabled}
          className={cn(
            isRecording &&
              'border border-border-strong focus-visible:ring-1 focus-visible:ring-accent',
            disabled && 'cursor-not-allowed'
          )}
          placeholder="Click to record shortcut"
        />
      )}
    </div>
  );
};
